import AsyncStorage from '@react-native-async-storage/async-storage';
import { apiCache } from '../services/apiCache';

/**
 * Comprehensive cache manager to handle all types of caches in the application
 * This ensures that when users logout/login, no stale data persists
 */

// Store UserDataContext clear function reference
let userDataContextClearFn: (() => void) | null = null;

export const registerUserDataContextClear = (clearFn: () => void) => {
  userDataContextClearFn = clearFn;
  console.log('✅ UserDataContext clear function registered with cacheManager');
};

class CacheManager {
  /**
   * Clear all API caches (in-memory caches)
   * NOTE: API caching is currently disabled
   */
  clearApiCache(): void {
    try {
      console.log('🧹 Clearing API cache (currently disabled)...');
      apiCache.clearAllCache();
      console.log('✅ API cache cleared successfully');
    } catch (error) {
      console.error('❌ Error clearing API cache:', error);
    }
  }

  /**
   * Clear all AsyncStorage data (persistent storage)
   */
  async clearAsyncStorage(): Promise<void> {
    try {
      console.log('🧹 Clearing AsyncStorage...');
      await AsyncStorage.clear();
      console.log('✅ AsyncStorage cleared successfully');
    } catch (error) {
      console.error('❌ Error clearing AsyncStorage:', error);
    }
  }

  /**
   * Clear specific AsyncStorage keys if complete clear is not desired
   */
  async clearSpecificAsyncStorageKeys(): Promise<void> {
    try {
      console.log('🧹 Clearing specific AsyncStorage keys...');
      const keysToRemove = [
        '@loginData',
        '@eventID',
        '@userBio',
        '@sessionID',
        '@locationDetails',
        'profilePicture',
        'userID'
      ];

      await AsyncStorage.multiRemove(keysToRemove);
      console.log('✅ Specific AsyncStorage keys cleared successfully');
    } catch (error) {
      console.error('❌ Error clearing specific AsyncStorage keys:', error);
    }
  }

  /**
   * Clear browser cache and local storage (for web platform)
   */
  clearWebCache(): void {
    try {
      if (typeof window !== 'undefined') {
        console.log('🧹 Clearing web cache...');
        
        // Clear localStorage
        if (window.localStorage) {
          window.localStorage.clear();
        }

        // Clear sessionStorage
        if (window.sessionStorage) {
          window.sessionStorage.clear();
        }

        // Clear any cached fetch requests if service worker is available
        if ('serviceWorker' in navigator && 'caches' in window) {
          caches.keys().then(cacheNames => {
            return Promise.all(
              cacheNames.map(cacheName => {
                return caches.delete(cacheName);
              })
            );
          });
        }

        console.log('✅ Web cache cleared successfully');
      }
    } catch (error) {
      console.error('❌ Error clearing web cache:', error);
    }
  }

  /**
   * Comprehensive cache clearing - clears ALL caches
   * Use this during logout to ensure no stale data remains
   */
  async clearAllCaches(): Promise<void> {
    console.log('🚀 Starting comprehensive cache clearing...');

    try {
      // Clear UserDataContext state first
      if (userDataContextClearFn) {
        console.log('🧹 Clearing UserDataContext via registered function...');
        userDataContextClearFn();
      } else {
        console.warn('⚠️ UserDataContext clear function not registered');
      }

      // Clear API cache (in-memory)
      this.clearApiCache();

      // Clear AsyncStorage (persistent storage)
      await this.clearAsyncStorage();

      // Clear web-specific caches
      this.clearWebCache();

      console.log('✅ All caches cleared successfully');
    } catch (error) {
      console.error('❌ Error during comprehensive cache clearing:', error);
      throw error;
    }
  }

  /**
   * Selective cache clearing - only clears API cache and user-specific data
   * Use this for less aggressive clearing scenarios
   */
  async clearUserSpecificCaches(): Promise<void> {
    console.log('🔄 Starting selective cache clearing...');
    
    try {
      // Clear API cache
      this.clearApiCache();

      // Clear only specific user-related AsyncStorage keys
      await this.clearSpecificAsyncStorageKeys();

      console.log('✅ User-specific caches cleared successfully');
    } catch (error) {
      console.error('❌ Error during selective cache clearing:', error);
      throw error;
    }
  }

  /**
   * Clear only API-related caches (for scenarios where user wants to refresh data)
   */
  clearOnlyApiCaches(): void {
    console.log('🔄 Clearing only API caches...');
    this.clearApiCache();
    console.log('✅ API caches cleared successfully');
  }
}

// Export singleton instance
export const cacheManager = new CacheManager();

// Export individual methods for convenience
export const clearAllCaches = () => cacheManager.clearAllCaches();
export const clearUserSpecificCaches = () => cacheManager.clearUserSpecificCaches();
export const clearOnlyApiCaches = () => cacheManager.clearOnlyApiCaches();
